/**
 * The command center's three chart forms, drawn as plain SVG so their marks
 * can be as thin as the reference: a hairline daily column chart with a
 * smooth comparison line, a grouped column chart, and a horizontal bar list.
 *
 * Colour comes from the page's `--cc-*` tokens (CommandCenterPage), which are
 * mixed from the theme's own foreground, card and destructive colours, so
 * every theme and both modes paint their own version. Text never wears a
 * series colour; marks carry identity and the legend names them.
 *
 * Every chart answers hover and keyboard focus with the same readout, and
 * every value is also in the card's table view, so the tooltip never gates.
 *
 * @module components/commandCenter/CommandCenterCharts
 */
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";

import { cn } from "../../lib/utils";
import { axisScale, monotonePath } from "./commandCenterLogic";

/** Hidden pattern definitions, rendered once per page and referenced by id. */
export function ChartPatterns() {
  // A fixed scatter, so the grain never shimmers between renders.
  const grain = [
    [0.8, 1.2],
    [3.4, 0.6],
    [5.9, 2.1],
    [1.9, 3.6],
    [4.6, 4.1],
    [6.6, 5.4],
    [0.5, 5.9],
    [2.8, 6.3],
    [5.2, 6.8],
    [3.9, 2.7],
    [6.2, 0.3],
    [1.3, 4.9],
  ] as const;
  return (
    <svg aria-hidden width="0" height="0" className="absolute">
      <defs>
        <pattern id="cc-grain" width="7" height="7" patternUnits="userSpaceOnUse">
          {grain.map(([x, y]) => (
            <circle
              key={`${x}-${y}`}
              cx={x}
              cy={y}
              r="0.55"
              style={{ fill: "var(--card)", opacity: 0.32 }}
            />
          ))}
        </pattern>
        <pattern
          id="cc-hatch"
          width="4"
          height="4"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="4"
            strokeWidth="1.2"
            style={{ stroke: "var(--card)", opacity: 0.28 }}
          />
        </pattern>
        <pattern id="cc-dots" width="3" height="3" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="0.7" style={{ fill: "var(--cc-ink)" }} />
        </pattern>
      </defs>
    </svg>
  );
}

/** The element's content box, tracked as it resizes. */
function useSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    setSize({ width: element.clientWidth, height: element.clientHeight });
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setSize({ width: Math.round(rect.width), height: Math.round(rect.height) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, size] as const;
}

/** A readout pinned above the hovered mark, flipped inward near the edges. */
function Readout({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  const nearRight = x > width - 140;
  const nearLeft = x < 140;
  return (
    <div
      role="status"
      className={cn(
        "pointer-events-none absolute top-0 z-10 min-w-36 rounded-lg border border-border/60 bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md",
        nearRight ? "-translate-x-full" : nearLeft ? "" : "-translate-x-1/2",
      )}
      style={{ left: nearRight ? x - 10 : nearLeft ? x + 10 : x }}
    >
      {children}
    </div>
  );
}

/** One readout row: the value leads, the series name follows; a short stroke keys it. */
export function ReadoutRow({
  swatch,
  label,
  value,
}: {
  swatch: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      {swatch}
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

export const strokeKey = (color: string, dotted = false) => (
  <span
    aria-hidden
    className="h-[2px] w-3 shrink-0 rounded-full"
    style={
      dotted
        ? {
            backgroundImage: `radial-gradient(circle, ${color} 0.8px, transparent 1px)`,
            backgroundSize: "3px 2px",
          }
        : { background: color }
    }
  />
);

/** Index the pointer is over, for a row of equal slots. */
const slotAt = (event: PointerEvent<Element>, slots: number) => {
  const rect = event.currentTarget.getBoundingClientRect();
  const x = event.clientX - rect.left;
  return Math.max(0, Math.min(slots - 1, Math.floor((x / Math.max(1, rect.width)) * slots)));
};

/** Arrow keys walk the marks; Home and End jump; Escape lets go. */
function useKeyboardWalk(
  count: number,
  setActive: (update: (index: number | null) => number | null) => void,
) {
  return useCallback(
    (event: KeyboardEvent<Element>) => {
      const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
      if (step !== 0) {
        event.preventDefault();
        setActive((index) =>
          Math.max(0, Math.min(count - 1, (index ?? (step > 0 ? -1 : count)) + step)),
        );
      } else if (event.key === "Home" || event.key === "End") {
        event.preventDefault();
        setActive(() => (event.key === "Home" ? 0 : count - 1));
      } else if (event.key === "Escape") {
        setActive(() => null);
      }
    },
    [count, setActive],
  );
}

// ── Area chart with a comparison line ─────────────────────────────────

export interface AreaChartProps {
  readonly labels: ReadonlyArray<string>;
  readonly values: ReadonlyArray<number>;
  /** Drawn as a thin muted line behind the area. */
  readonly comparison: ReadonlyArray<number>;
  readonly ticks: ReadonlyArray<{ readonly index: number; readonly label: string }>;
  readonly seriesLabel: string;
  readonly comparisonLabel: string;
  readonly format: (value: number) => string;
  /** The raw comparison value per position, for the readout. */
  readonly comparisonRaw?: ReadonlyArray<number>;
  /** When `values` is an average: each day's own figure, for the readout. */
  readonly valuesRaw?: ReadonlyArray<number> | undefined;
  /** What the drawn (averaged) value is called in the readout. */
  readonly averageLabel?: string | undefined;
  /** The series colour: a CSS colour or var(); the fill is a fade of it. */
  readonly color: string;
  readonly ariaLabel: string;
  /** Fixed height; when absent the chart fills its container (at least 200px). */
  readonly height?: number;
}

const AXIS_BAND = 22;
const TOP_PAD = 12;

/**
 * One long, smooth series over days: a 2px line in the series colour over a
 * fade of it, the previous period as a thin muted line behind. Monotone
 * smoothing never overshoots, so a quiet day never dips below zero.
 */
export function AreaChart({
  labels,
  values,
  comparison,
  comparisonRaw,
  valuesRaw,
  averageLabel,
  ticks,
  seriesLabel,
  comparisonLabel,
  format,
  color,
  ariaLabel,
  height: fixedHeight,
}: AreaChartProps) {
  // An id safe inside url(#…): React's ids carry colons.
  const gradientId = `cc-area-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [ref, size] = useSize<HTMLDivElement>();
  const width = size.width;
  const height = fixedHeight ?? Math.max(200, size.height);
  const [active, setActive] = useState<number | null>(null);
  const count = values.length;
  const onKeyDown = useKeyboardWalk(count, setActive);
  const plot = height - AXIS_BAND - TOP_PAD;
  const base = TOP_PAD + plot;
  const max = Math.max(1, ...values, ...comparison) * 1.08;
  const step = count > 1 ? width / (count - 1) : 0;
  const x = (index: number) => (count > 1 ? index * step : width / 2);
  const y = (value: number) => TOP_PAD + plot - (Math.max(0, value) / max) * plot;
  const line = monotonePath(values.map((value, index) => [x(index), y(value)] as const));
  const area =
    count > 0 ? `${line} L${x(count - 1).toFixed(2)},${base} L${x(0).toFixed(2)},${base} Z` : "";
  const previous = monotonePath(comparison.map((value, index) => [x(index), y(value)] as const));
  const nearest = (event: PointerEvent<Element>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return Math.max(
      0,
      Math.min(count - 1, Math.round((event.clientX - rect.left) / Math.max(1, step))),
    );
  };

  return (
    <div
      ref={ref}
      className={cn("relative w-full", fixedHeight === undefined && "min-h-[200px] flex-1")}
      style={fixedHeight === undefined ? undefined : { height: fixedHeight }}
    >
      {width > 0 && (
        <svg
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          width={width}
          height={height}
          className="absolute inset-0 block touch-pan-y overflow-visible rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onPointerMove={(event) => setActive(nearest(event))}
          onPointerLeave={() => setActive(null)}
          onKeyDown={onKeyDown}
          onBlur={() => setActive(null)}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.28 }} />
              <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
            </linearGradient>
          </defs>
          {[0.5, 1].map((fraction) => (
            <line
              key={fraction}
              x1={0}
              x2={width}
              y1={Math.round(base - fraction * plot) + 0.5}
              y2={Math.round(base - fraction * plot) + 0.5}
              style={{ stroke: "var(--cc-grid)" }}
            />
          ))}
          <line
            x1={0}
            x2={width}
            y1={base + 0.5}
            y2={base + 0.5}
            style={{ stroke: "var(--cc-grid)" }}
          />
          {/* The previous period sits behind, thin and quiet; the area carries the story. */}
          <path
            d={previous}
            fill="none"
            strokeWidth={1}
            strokeLinecap="round"
            style={{ stroke: "var(--foreground)", opacity: 0.35 }}
          />
          <path d={area} style={{ fill: `url(#${gradientId})` }} />
          <path
            d={line}
            fill="none"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ stroke: color }}
          />
          {active !== null && (
            <>
              <line
                x1={x(active)}
                x2={x(active)}
                y1={TOP_PAD}
                y2={base}
                strokeWidth={1}
                style={{ stroke: "var(--foreground)", opacity: 0.25 }}
              />
              <circle
                cx={x(active)}
                cy={y(values[active] ?? 0)}
                r={4}
                strokeWidth={2}
                style={{ fill: color, stroke: "var(--card)" }}
              />
            </>
          )}
          {ticks.map((tick) => (
            <text
              key={`${tick.index}-${tick.label}`}
              x={Math.min(Math.max(x(tick.index), 14), width - 14)}
              y={height - 5}
              textAnchor="middle"
              className="fill-muted-foreground text-[11px] tabular-nums"
            >
              {tick.label}
            </text>
          ))}
        </svg>
      )}
      {active !== null && width > 0 && (
        <Readout x={x(active)} width={width}>
          <div className="mb-1 font-medium text-foreground">{labels[active]}</div>
          <ReadoutRow
            swatch={strokeKey(color)}
            label={seriesLabel}
            value={format((valuesRaw ?? values)[active] ?? 0)}
          />
          {valuesRaw && averageLabel ? (
            <ReadoutRow
              swatch={strokeKey(color)}
              label={averageLabel}
              value={(values[active] ?? 0).toLocaleString("en-GB", { maximumFractionDigits: 1 })}
            />
          ) : null}
          <ReadoutRow
            swatch={strokeKey("color-mix(in oklab, var(--foreground) 35%, transparent)")}
            label={comparisonLabel}
            value={format((comparisonRaw ?? comparison)[active] ?? 0)}
          />
        </Readout>
      )}
    </div>
  );
}

// ── Grouped columns ────────────────────────────────────────────────────

export interface ColumnSeries {
  readonly key: string;
  readonly label: string;
  /** Solid marks carry grain; dotted marks are the comparison. */
  readonly style: "solid" | "dotted";
}

export interface ColumnCategory {
  readonly key: string;
  readonly label: string;
  /** Long form for the readout. */
  readonly title?: string;
  /** Said on the baseline when the category has nothing to draw ("No data"). */
  readonly empty?: string;
  readonly values: ReadonlyArray<{
    readonly value: number;
    readonly display: string;
    readonly detail?: string;
    readonly attention?: boolean;
  }>;
}

export interface ColumnChartProps {
  readonly series: ReadonlyArray<ColumnSeries>;
  readonly categories: ReadonlyArray<ColumnCategory>;
  /** Fixed top of the scale (1 for shares); computed when absent. */
  readonly max?: number;
  readonly formatTick: (value: number) => string;
  /** Show at least every nth category label. */
  readonly labelEvery?: number;
  /**
   * What crowded labels do: a run of dates thins out (every other week),
   * named categories alternate between two rows so each keeps its whole
   * name, horizontal.
   */
  readonly crowding?: "thin" | "stagger";
  readonly ariaLabel: string;
  /** Fixed height; when absent the chart fills its container (at least 200px). */
  readonly height?: number;
}

const Y_AXIS = 38;

/** A column with a rounded data end and a square foot on the baseline. */
const columnPath = (x: number, top: number, width: number, base: number) => {
  const r = Math.min(3, width / 2, Math.max(0, base - top));
  return `M${x},${base} V${top + r} Q${x},${top} ${x + r},${top} H${x + width - r} Q${x + width},${top} ${x + width},${top + r} V${base} Z`;
};

export function ColumnChart({
  series,
  categories,
  max: fixedMax,
  formatTick,
  labelEvery: minimumEvery = 1,
  crowding = "thin",
  ariaLabel,
  height: fixedHeight,
}: ColumnChartProps) {
  const [ref, size] = useSize<HTMLDivElement>();
  const width = size.width;
  const height = fixedHeight ?? Math.max(200, size.height);
  const [active, setActive] = useState<number | null>(null);
  const count = categories.length;
  const onKeyDown = useKeyboardWalk(count, setActive);
  const inner = Math.max(0, width - Y_AXIS);
  const slot = inner / Math.max(1, count);
  const longest = Math.max(1, ...categories.map((category) => category.label.length));
  // About 6.2px per character at 11px; a label needs its slot plus a little air.
  const crowded = width > 0 && longest * 6.2 + 6 > slot;
  const stagger = crowding === "stagger" && crowded;
  const labelEvery =
    crowding === "thin" && crowded
      ? Math.max(minimumEvery, Math.ceil((longest * 6.2 + 10) / Math.max(1, slot)))
      : minimumEvery;
  const axisBand = stagger ? AXIS_BAND + 14 : AXIS_BAND;
  const plot = height - axisBand - TOP_PAD;
  const largest = Math.max(
    0,
    ...categories.flatMap((category) => category.values.map((entry) => entry.value)),
  );
  const scale =
    fixedMax === undefined
      ? axisScale(largest)
      : { max: fixedMax, ticks: [0, 0.25, 0.5, 0.75, 1].map((f) => f * fixedMax) };
  const { max, ticks } = scale;
  const gap = 3;
  // Few categories keep air between groups; a long run of weeks packs tighter.
  const fill = count > 12 ? 0.72 : 0.56;
  const barWidth = Math.max(
    2,
    Math.min(16, (slot * fill - gap * (series.length - 1)) / Math.max(1, series.length)),
  );
  const group = barWidth * series.length + gap * (series.length - 1);
  const y = (value: number) => TOP_PAD + plot - (Math.max(0, value) / max) * plot;
  const base = TOP_PAD + plot;

  return (
    <div
      ref={ref}
      className={cn("relative w-full", fixedHeight === undefined && "min-h-[200px] flex-1")}
      style={fixedHeight === undefined ? undefined : { height: fixedHeight }}
    >
      {width > 0 && (
        <svg
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          width={width}
          height={height}
          className="absolute inset-0 block touch-pan-y overflow-visible rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onPointerLeave={() => setActive(null)}
          onKeyDown={onKeyDown}
          onBlur={() => setActive(null)}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={Y_AXIS}
                x2={width}
                y1={Math.round(y(tick)) + 0.5}
                y2={Math.round(y(tick)) + 0.5}
                style={{ stroke: "var(--cc-grid)" }}
              />
              <text
                x={0}
                y={y(tick) + 4}
                className="fill-muted-foreground text-[11px] tabular-nums"
              >
                {formatTick(tick)}
              </text>
            </g>
          ))}
          <g
            transform={`translate(${Y_AXIS},0)`}
            onPointerMove={(event) => setActive(slotAt(event, count))}
          >
            <rect x={0} y={0} width={inner} height={height} fill="transparent" />
            {categories.map((category, index) => {
              const left = slot * index + (slot - group) / 2;
              return (
                <g
                  key={category.key}
                  style={{ opacity: active === null || active === index ? 1 : 0.55 }}
                  className="transition-opacity duration-150"
                >
                  {category.values.map((entry, seriesIndex) => {
                    const kind = series[seriesIndex]?.style ?? "solid";
                    const bx = left + seriesIndex * (barWidth + gap);
                    const top = entry.value > 0 ? Math.min(y(entry.value), base - 2) : base - 1;
                    const d = columnPath(bx, top, barWidth, base);
                    return kind === "dotted" ? (
                      <g key={seriesIndex}>
                        <path d={d} style={{ fill: "var(--cc-faint)", opacity: 0.45 }} />
                        <path d={d} fill="url(#cc-dots)" />
                      </g>
                    ) : (
                      <g key={seriesIndex}>
                        <path
                          d={d}
                          style={{
                            fill: entry.attention ? "var(--cc-attention)" : "var(--cc-ink)",
                          }}
                        />
                        <path d={d} fill="url(#cc-hatch)" />
                        <path d={d} fill="url(#cc-grain)" />
                      </g>
                    );
                  })}
                  {category.empty && (
                    // Said on the baseline of the empty slot, quietly, where its bars would stand.
                    <text
                      x={slot * index + slot / 2}
                      y={base - 5}
                      textAnchor="middle"
                      className="fill-muted-foreground/70 text-[10px]"
                    >
                      {category.empty}
                    </text>
                  )}
                  {index % labelEvery === 0 && (
                    <text
                      x={slot * index + slot / 2}
                      y={base + 17 + (stagger && index % 2 === 1 ? 14 : 0)}
                      textAnchor="middle"
                      className="fill-muted-foreground text-[11px]"
                    >
                      {category.label}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      )}
      {active !== null && width > 0 && categories[active] && (
        <Readout x={Y_AXIS + slot * (active + 0.5)} width={width}>
          <div className="mb-1 font-medium text-foreground">
            {categories[active].title ?? categories[active].label}
          </div>
          {categories[active].values.map((entry, seriesIndex) => {
            const kind = series[seriesIndex];
            return (
              <ReadoutRow
                key={kind?.key ?? seriesIndex}
                swatch={strokeKey(
                  kind?.style === "dotted"
                    ? "var(--cc-ink)"
                    : entry.attention
                      ? "var(--cc-attention)"
                      : "var(--cc-ink)",
                  kind?.style === "dotted",
                )}
                label={
                  entry.detail ? `${kind?.label ?? ""} · ${entry.detail}` : (kind?.label ?? "")
                }
                value={entry.display}
              />
            );
          })}
        </Readout>
      )}
    </div>
  );
}

// ── Horizontal bar list ────────────────────────────────────────────────

export interface BarListRow {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly display: string;
  readonly attention?: boolean;
}

export function BarList({
  rows,
  max: fixedMax,
  ariaLabel,
}: {
  rows: ReadonlyArray<BarListRow>;
  max?: number;
  ariaLabel: string;
}) {
  const max = fixedMax ?? Math.max(1, ...rows.map((row) => row.value));
  return (
    <ul aria-label={ariaLabel} className="grid gap-4">
      {rows.map((row) => {
        const share = Math.max(0, Math.min(1, row.value / max));
        return (
          <li
            key={row.key}
            className="grid grid-cols-[minmax(0,8.5rem)_minmax(0,1fr)] items-center gap-4 text-[13px] sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]"
          >
            <span className="truncate text-muted-foreground" title={row.label}>
              {row.label}
            </span>
            <span className="flex min-w-0 items-center gap-2.5">
              {share > 0 ? (
                // The longest bar leaves room for its own label beside it.
                <svg
                  aria-hidden
                  className="h-3.5 shrink-0 overflow-visible"
                  style={{ width: `calc((100% - 4.5rem) * ${share})` }}
                >
                  <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="14"
                    rx="3"
                    style={{ fill: row.attention ? "var(--cc-attention)" : "var(--cc-ink)" }}
                  />
                  <rect x="0" y="0" width="100%" height="14" rx="3" fill="url(#cc-hatch)" />
                  <rect x="0" y="0" width="100%" height="14" rx="3" fill="url(#cc-grain)" />
                </svg>
              ) : (
                <span aria-hidden className="h-px w-2 shrink-0 bg-[var(--cc-faint)]" />
              )}
              <span className="shrink-0 tabular-nums text-muted-foreground">{row.display}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
